/**
 * pairings.ts - Pairings Module
 *
 * This module handles pairing generation and management including:
 * - Generating pairings for Swiss, Round-Robin, and Knockout formats
 * - Deleting rounds
 * - Printing matches
 */

import type { Tournament, Round } from '../../types/api';
import { TOURNAMENT_FORMATS, API_BASE_URL } from '../utils/constants';
import { escapeHtml } from '../utils/security';
import { t } from '../utils/i18n-helper';
import {
  getPrintHeaderStyles,
  getPrintTableStyles,
  generateTournamentHeader,
  generatePrintFooter,
  showPrintOptionsDialog
} from './print-utils';
import { formatRoundDateTime } from '../utils/formatting';
import { showConfirmDialog } from '../utils/confirm-dialog';

/**
 * Pairing generation result
 */
interface PairingResult {
  round: number;
  message?: string;
}

/**
 * Delete round result
 */
interface DeleteRoundResult {
  newCurrentRound: number;
  message?: string;
}

/**
 * Generate pairings for the next round
 * @param tournamentId - The tournament ID
 */
export function generatePairings(tournamentId: string): Promise<void> {
  // Generate pairings directly without round info modal
  // Round dates/times are now managed in the Schedule tab
  return generatePairingsWithRoundInfo(tournamentId, null, null);
}

/**
 * Show loading indicator
 * @param tournamentId - The tournament ID
 */
function showLoadingIndicator(tournamentId: string): void {
  // Find the generate button
  const generateBtn = document.getElementById(`generate-pairings-${tournamentId}`) as HTMLButtonElement;
  if (generateBtn) {
    generateBtn.disabled = true;
    generateBtn.style.cursor = 'wait';
    generateBtn.style.opacity = '0.6';
    generateBtn.setAttribute('data-original-text', generateBtn.innerHTML);
    const loadingText = window.i18n ? window.i18n.t('pairings.calculating') : 'Calculating pairings...';
    generateBtn.innerHTML = loadingText;
  }
}

/**
 * Hide loading indicator
 * @param tournamentId - The tournament ID
 */
function hideLoadingIndicator(tournamentId: string): void {
  const generateBtn = document.getElementById(`generate-pairings-${tournamentId}`) as HTMLButtonElement;
  if (generateBtn) {
    generateBtn.disabled = false;
    generateBtn.style.cursor = 'pointer';
    generateBtn.style.opacity = '1';
    const originalText = generateBtn.getAttribute('data-original-text');
    if (originalText) {
      generateBtn.innerHTML = originalText;
      generateBtn.removeAttribute('data-original-text');
    }
  }
}

/**
 * Generate pairings with optional round info
 * @param tournamentId - The tournament ID
 * @param roundDate - Optional round date
 * @param startTime - Optional start time
 */
export async function generatePairingsWithRoundInfo(
  tournamentId: string,
  roundDate: string | null,
  startTime: string | null
): Promise<void> {
  // Disable logging in production
  const enableLogging = false;

  // Show loading indicator
  showLoadingIndicator(tournamentId);

  try {
    const response = await fetch(`${API_BASE_URL}/${tournamentId}/pairings`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        enableLogging,
        roundDate: roundDate || undefined,
        startTime: startTime || undefined
      }),
    });

    if (response.ok) {
      const result: PairingResult = await response.json();
      const generatedRound = result.round;

      // Save active tab before reload
      sessionStorage.setItem(`active-tab-${tournamentId}`, 'pairings');

      // Reload single tournament
      if (window.loadSingleTournament) {
        await window.loadSingleTournament(tournamentId);
      }

      // After reload, show the newly generated round (if it exists)
      if (generatedRound && generatedRound > 0) {
        setTimeout(() => {
          if (window.showMatchRound) {
            window.showMatchRound(tournamentId, generatedRound);
          }
        }, 50);
      }
    } else {
      const error = await response.json();
      // Check if the error is a translation key
      let errorMessage = error.error;
      if (window.i18n && error.error && error.error.startsWith('errors.')) {
        // Try to translate it
        const translated = window.i18n.t(error.error, error.count ? { count: error.count } : {});
        if (translated !== error.error) {
          errorMessage = translated;
        }
      }
      alert(`Error: ${errorMessage}`);
    }
  } catch (error) {
    console.error('Error generating pairings:', error);
    const errorMsg = window.i18n ? window.i18n.t('pairings.errorGenerating') : 'Error generating pairings';
    alert(errorMsg);
  } finally {
    // Always hide loading indicator
    hideLoadingIndicator(tournamentId);
  }
}

/**
 * Generate round-robin pairings
 * @param tournamentId - The tournament ID
 * @param doubleRoundRobin - Whether to use double round-robin
 */
export async function generateRoundRobinPairings(
  tournamentId: string,
  doubleRoundRobin: boolean
): Promise<void> {
  // Show loading indicator
  showLoadingIndicator(tournamentId);

  try {
    // First, update the tournament with the doubleRoundRobin setting
    const updateResponse = await fetch(`${API_BASE_URL}/${tournamentId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ doubleRoundRobin }),
    });

    if (!updateResponse.ok) {
      throw new Error('Failed to update tournament');
    }

    // Then generate pairings
    const response = await fetch(`${API_BASE_URL}/${tournamentId}/pairings`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({}),
    });

    if (response.ok) {
      const result: PairingResult = await response.json();
      const generatedRound = result.round;

      // Save active tab before reload
      sessionStorage.setItem(`active-tab-${tournamentId}`, 'pairings');

      // Reload single tournament
      if (window.loadSingleTournament) {
        await window.loadSingleTournament(tournamentId);
      }

      // After reload, show the newly generated round (if it exists)
      if (generatedRound && generatedRound > 0) {
        setTimeout(() => {
          if (window.showMatchRound) {
            window.showMatchRound(tournamentId, generatedRound);
          }
        }, 50);
      }
    } else {
      const error = await response.json();
      // Check if the error is a translation key
      let errorMessage = error.error;
      if (window.i18n && error.error && error.error.startsWith('errors.')) {
        // Try to translate it
        const translated = window.i18n.t(error.error, error.count ? { count: error.count } : {});
        if (translated !== error.error) {
          errorMessage = translated;
        }
      }
      alert(`Error: ${errorMessage}`);
    }
  } catch (error) {
    console.error('Error generating round-robin pairings:', error);
    const errorMsg = window.i18n ? window.i18n.t('pairings.errorGenerating') : 'Error generating pairings';
    alert(errorMsg);
  } finally {
    // Always hide loading indicator
    hideLoadingIndicator(tournamentId);
  }
}

/**
 * Delete the latest round
 * @param tournamentId - The tournament ID
 */
export async function deleteLatestRound(tournamentId: string): Promise<void> {
  // First, fetch the tournament to check its format
  try {
    const tournamentResponse = await fetch(`${API_BASE_URL}/${tournamentId}`, {
      credentials: 'include'
    });

    if (!tournamentResponse.ok) {
      const errorMsg = window.i18n ? window.i18n.t('pairings.errorLoadingData') : 'Error loading tournament data';
      alert(errorMsg);
      return;
    }

    const tournament: Tournament = await tournamentResponse.json();

    // Calculate the highest round from matches instead of using currentRound field
    const highestRound = tournament.matches && tournament.matches.length > 0
      ? Math.max(...tournament.matches.map(m => m.round))
      : 0;

    // Show confirmation dialog
    const isRoundRobin = tournament.format === TOURNAMENT_FORMATS.ROUND_ROBIN;
    let confirmMessage: string;

    if (isRoundRobin) {
      confirmMessage = window.i18n
        ? window.i18n.t('pairings.confirmDeleteAllRounds')
        : 'Are you sure you want to delete all rounds? This cannot be undone.';
    } else {
      confirmMessage = window.i18n
        ? window.i18n.t('pairings.confirmDeleteRound', { round: highestRound })
        : `Are you sure you want to delete Round ${highestRound}? This cannot be undone.`;
    }

    const confirmed = await showConfirmDialog(confirmMessage);

    if (!confirmed) {
      return;
    }

    const response = await fetch(`${API_BASE_URL}/${tournamentId}/rounds/latest`, {
      method: 'DELETE',
      credentials: 'include'
    });

    if (response.ok) {
      const data: DeleteRoundResult = await response.json();
      const newCurrentRound = data.newCurrentRound;

      // Save active tab before reload
      sessionStorage.setItem(`active-tab-${tournamentId}`, 'pairings');

      // Reload single tournament and then show the new latest round
      if (window.loadSingleTournament) {
        await window.loadSingleTournament(tournamentId);
      }

      // After reload, show the new latest round (if it exists)
      if (newCurrentRound && newCurrentRound > 0) {
        setTimeout(() => {
          if (window.showMatchRound) {
            window.showMatchRound(tournamentId, newCurrentRound);
          }
        }, 50);
      }
    } else {
      const error = await response.json();
      // Check if the error is a translation key
      let errorMessage = error.error;
      if (window.i18n && error.error && error.error.startsWith('errors.')) {
        // Try to translate it
        const translated = window.i18n.t(error.error, error.count ? { count: error.count } : {});
        if (translated !== error.error) {
          errorMessage = translated;
        }
      }
      alert(`Error: ${errorMessage}`);
    }
  } catch (error) {
    console.error('Error deleting round:', error);
    const errorMsg = window.i18n ? window.i18n.t('pairings.errorDeleting') : 'Error deleting round';
    alert(errorMsg);
  }
}

/**
 * Print matches for a tournament
 * @param tournamentId - The tournament ID
 */
export async function printMatches(tournamentId: string): Promise<void> {
  const matchesDiv = document.getElementById(`pairings-tab-${tournamentId}`);
  if (!matchesDiv) return;

  // Show print options dialog
  const printOptions = await showPrintOptionsDialog();
  if (!printOptions) return; // User cancelled

  // Fetch tournament data to get all fields
  let tournament: Tournament;
  try {
    const response = await fetch(`${API_BASE_URL}/${tournamentId}`, {
      credentials: 'include'
    });
    tournament = await response.json();
  } catch (error) {
    console.error('Error fetching tournament:', error);
    const errorMsg = window.i18n ? window.i18n.t('pairings.printDataLoadFailed') : 'Failed to load tournament data for printing';
    alert(errorMsg);
    return;
  }

  // Fetch rounds information to get dates/times
  let rounds: Round[] = [];
  try {
    const roundsResponse = await fetch(`${API_BASE_URL}/${tournamentId}/rounds`, {
      credentials: 'include'
    });
    rounds = await roundsResponse.json();
  } catch (error) {
    console.error('Error fetching rounds:', error);
  }

  // Determine which round is being displayed
  // Use data-round attribute instead of parsing text (language-independent)
  let roundNumber: number | null = null;

  // First, check for active round button with data-round attribute
  const activeRoundBtn = matchesDiv.querySelector<HTMLElement>('.round-selector .btn-primary[data-round]');
  if (activeRoundBtn) {
    const dataRound = activeRoundBtn.getAttribute('data-round');
    if (dataRound) {
      roundNumber = parseInt(dataRound, 10);
    }
  }

  // If no active button found, check for any visible round content
  if (!roundNumber) {
    const visibleRoundContent = matchesDiv.querySelector<HTMLElement>('.round-content[style*="display: block"], .round-content:not([style*="display: none"])');
    if (visibleRoundContent && visibleRoundContent.id) {
      // ID format: match-round-{tournamentId}-{roundNumber}
      const idMatch = visibleRoundContent.id.match(/match-round-[^-]+-(\d+)$/);
      if (idMatch && idMatch[1]) {
        roundNumber = parseInt(idMatch[1], 10);
      }
    }
  }

  // Fallback to current round if we couldn't detect it from UI
  if (!roundNumber && tournament.currentRound) {
    roundNumber = tournament.currentRound;
  }

  // Get schedule info for the current round
  let scheduleInfo = '';
  if (roundNumber && rounds.length > 0) {
    const currentRound = rounds.find(r => r.roundNumber === roundNumber);
    if (currentRound && currentRound.roundDate) {
      scheduleInfo = formatRoundDateTime(currentRound.roundDate, currentRound.startTime || undefined);
    }
  }

  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    const errorMsg = window.i18n ? window.i18n.t('pairings.printWindowBlocked') : 'Print window was blocked. Please allow popups for this site and try again.';
    alert(errorMsg);
    return;
  }

  printWindow.document.write(`
    <html>
      <head>
        <title>${tournament.name} - ${t('pairings.title', 'Pairings')}</title>
        <style>
          ${getPrintHeaderStyles()}
          ${getPrintTableStyles()}

          /* Document Title */
          .document-title {
            display: flex;
            justify-content: space-between;
            align-items: baseline;
          }

          .document-title .schedule-inline {
            font-size: 13px;
            font-weight: 400;
            color: #5a5a5a;
            letter-spacing: 0;
          }

          /* Round Section */
          .round-section {
            margin-bottom: 0;
          }

          .round-section h5 {
            display: none;
          }

          /* Specific column widths for pairings */
          td {
            padding: 5px 10px;
            font-size: 10px;
            color: #1a1a1a;
            vertical-align: middle;
            line-height: 1.2;
          }

          th:nth-child(1), td:nth-child(1) {
            width: 60px;
            text-align: left;
          }

          td:nth-child(1) {
            font-weight: 600;
            color: #0a0a0a;
          }

          th:nth-child(2), td:nth-child(2) {
            width: 35%;
          }

          td:nth-child(2) {
            font-weight: 500;
          }

          /* VS column */
          th:nth-child(3), td:nth-child(3) {
            width: 30px;
            text-align: center;
          }

          td:nth-child(3) {
            color: #999;
            font-weight: 400;
          }

          th:nth-child(4), td:nth-child(4) {
            width: 35%;
          }

          td:nth-child(4) {
            font-weight: 500;
          }

          th:nth-child(5), td:nth-child(5) {
            width: 180px;
            text-align: left;
          }

          td:nth-child(5) {
            font-weight: 600;
            color: #0a0a0a;
          }

          /* Hide UI elements */
          .round-selector,
          .btn-icon,
          .results-entry-controls,
          button {
            display: none !important;
          }
        </style>
      </head>
      <body>
        ${generateTournamentHeader(tournament, printOptions)}

        <h2 class="document-title">
          <span>${t('pairings.title', 'Pairings')}${roundNumber ? ' - ' + t('round.round', 'Round') + ' ' + roundNumber : ''}</span>
          ${scheduleInfo ? `<span class="schedule-inline">${escapeHtml(scheduleInfo)}</span>` : ''}
        </h2>

        ${(() => {
          let html = matchesDiv.innerHTML
            .replace(/onclick="[^"]*"/g, '')
            .replace(/<select.*?<\/select>/gs, function(match) {
              const selected = match.match(/value="([^"]*)"[^>]*selected/);
              return selected && selected[1] ? selected[1] : '';
            })
            .replace(/0\.5-0\.5/g, '½-½')
            .replace(/<button[^>]*>.*?<\/button>/gs, '');

          // Add VS column to header (after 2nd th)
          html = html.replace(/<thead>[\s\S]*?<\/thead>/g, function(thead) {
            let thCount = 0;
            return thead.replace(/<\/th>/g, function(match) {
              thCount++;
              if (thCount === 2) {
                return match + '<th></th>';
              }
              return match;
            });
          });

          // Add VS column to each tbody row (after 2nd td)
          html = html.replace(/<tbody>[\s\S]*?<\/tbody>/g, function(tbody) {
            return tbody.replace(/<tr[\s\S]*?<\/tr>/g, function(row) {
              // Insert <td>–</td> after the 2nd </td> in each row
              let tdCount = 0;
              return row.replace(/<\/td>/g, function(match) {
                tdCount++;
                if (tdCount === 2) {
                  return match + '<td>–</td>';
                }
                return match;
              });
            });
          });

          return html;
        })()}

        ${generatePrintFooter(tournament)}

        <script>
          // Wait for images to load before printing
          var images = document.getElementsByTagName('img');
          var imagesToLoad = images.length;
          var imagesLoaded = 0;
          var printTriggered = false;

          function triggerPrint() {
            if (!printTriggered) {
              printTriggered = true;
              window.print();
              window.onafterprint = function() { window.close(); };
            }
          }

          if (imagesToLoad === 0) {
            triggerPrint();
          } else {
            // Set a timeout as fallback in case images fail to load
            setTimeout(triggerPrint, 2000);

            for (var i = 0; i < images.length; i++) {
              if (images[i].complete) {
                imagesLoaded++;
              } else {
                images[i].onload = function() {
                  imagesLoaded++;
                  if (imagesLoaded === imagesToLoad) {
                    triggerPrint();
                  }
                };
                images[i].onerror = function() {
                  imagesLoaded++;
                  if (imagesLoaded === imagesToLoad) {
                    triggerPrint();
                  }
                };
              }
            }
            // If all images were already complete
            if (imagesLoaded === imagesToLoad) {
              triggerPrint();
            }
          }
        </script>
      </body>
    </html>
  `);
  printWindow.document.close();
}

// Export to window for onclick handlers

window.generatePairings = generatePairings;
window.generatePairingsWithRoundInfo = generatePairingsWithRoundInfo;
window.generateRoundRobinPairings = generateRoundRobinPairings;
window.deleteLatestRound = deleteLatestRound;
window.printMatches = printMatches;
